import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import {
  DiscoveryAssistantMode,
  discoveryMessagesInfiniteQueryOptions,
  discoveryQueryKeys,
  type DiscoveryMessage,
  type DiscoveryMessageListResponse,
} from '@/entities/discovery';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

import { searchCreatorPendingAtom } from './mutation-atom';
import { useDiscoveryStore } from './discovery-store';

type DiscoveryMessagesData = InfiniteData<DiscoveryMessageListResponse, string | undefined>;

export const useDiscoveryMessagesActions = () => {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  const { session } = useDiscoveryStore();
  const sessionId = session?.session_id;

  const updateMessages = useCallback(
    (updater: (messages: DiscoveryMessage[]) => DiscoveryMessage[]) => {
      if (!sessionId) return;

      queryClient.setQueryData<DiscoveryMessagesData>(
        discoveryQueryKeys.messages(workspaceId, sessionId),
        (data) => updateDiscoveryMessagesData(data, updater),
      );
    },
    [queryClient, sessionId, workspaceId],
  );

  const appendMessages = useCallback(
    (messages: DiscoveryMessage[]) => {
      const incomingMessageIds = new Set(messages.map((message) => message.message_id));
      updateMessages((currentMessages) => [
        ...messages,
        ...currentMessages.filter((message) => !incomingMessageIds.has(message.message_id)),
      ]);
    },
    [updateMessages],
  );

  const updateMessage = useCallback(
    (messageId: string, nextMessage: DiscoveryMessage) => {
      updateMessages((messages) =>
        messages.map((message) => (message.message_id === messageId ? nextMessage : message)),
      );
    },
    [updateMessages],
  );

  const updateSearchMessageHistoryId = useCallback(
    (messageId: string, searchHistoryId: string) => {
      updateMessages((messages) =>
        messages.map((message) => {
          if (
            message.message_id !== messageId ||
            message.role !== 'assistant' ||
            message.assistant_mode !== DiscoveryAssistantMode.SEARCH
          ) {
            return message;
          }

          return { ...message, search_history_id: searchHistoryId };
        }),
      );
    },
    [updateMessages],
  );

  return { appendMessages, updateMessage, updateSearchMessageHistoryId };
};

export function useDiscoveryMessagesQuery(discoverySessionId: string) {
  const { workspaceId } = useWorkspaceId();
  const searchPending = useAtomValue(searchCreatorPendingAtom);
  const query = useInfiniteQuery({
    ...discoveryMessagesInfiniteQueryOptions(workspaceId, discoverySessionId),
    enabled: discoverySessionId.length > 0 && !searchPending,
  });
  const messages = query.data?.pages.flatMap((page) => page.messages) ?? [];

  return {
    ...query,
    messages,
    fetchNextPage: () => {
      if (searchPending || !query.hasNextPage || query.isFetchingNextPage) return;
      void query.fetchNextPage();
    },
  };
}

export function useDiscoveryMessages() {
  const { session } = useDiscoveryStore();
  return useDiscoveryMessagesQuery(session?.session_id ?? '').messages;
}

function updateDiscoveryMessagesData(
  data: DiscoveryMessagesData | undefined,
  updater: (messages: DiscoveryMessage[]) => DiscoveryMessage[],
): DiscoveryMessagesData {
  if (!data) {
    return {
      pages: [
        {
          messages: updater([]),
          next_cursor: null,
          has_more: false,
        },
      ],
      pageParams: [undefined],
    };
  }

  const messages = updater(data.pages.flatMap((page) => page.messages));
  return {
    ...data,
    pages: data.pages.map((page, index) => ({
      ...page,
      messages: index === 0 ? messages : [],
    })),
  };
}
